import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Phone, X, Send, Bot, Loader2, PhoneCall } from "lucide-react";
import { SITE, waLink } from "@/lib/site";
import { getBotAnswer } from "@/lib/chatBrain";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Free, rule-based assistant (no LLM / no API cost). Answers common questions
// with canned replies + links, and does keyword search over the catalogue.
const GREETING = {
  from: "bot",
  text: "Hello! I'm Trixie, the TripTrix assistant. Ask me anything — destinations, permits, transport or net rates — or tap a topic below.",
};

const QUICK = [
  { id: "packages", label: "Tour packages" },
  { id: "transport", label: "Vehicle / transport" },
  { id: "besttime", label: "Best time to visit" },
  { id: "netrates", label: "Get net rates" },
  { id: "callback", label: "Request a callback" },
  { id: "human", label: "Talk to a human" },
];

function botReply(id) {
  switch (id) {
    case "packages":
      return {
        from: "bot",
        text: "We handle North East, Sikkim, Darjeeling & Bhutan packages on B2B net rates only. Browse by theme or city:",
        links: [
          { label: "All tour packages", to: "/tours" },
          { label: "Explore everything", to: "/explore" },
        ],
        wa: "Hi TripTrix, I'd like net rates for a tour package.",
      };
    case "transport":
      return {
        from: "bot",
        text: "We arrange SUVs (Innova/Xylo), tempo travellers & sedans with drivers and permits across the region. Search a city below, or ping us on WhatsApp.",
        wa: "Hi TripTrix, I need vehicle/transport (car/tempo) — please share net rates.",
      };
    case "besttime":
      return {
        from: "bot",
        text: "Oct–Apr is great region-wide; Mar–May for blooms & clear skies, Dec–Mar for snow in Sikkim/Tawang, and the monsoon is stunning in Meghalaya. Read our guides:",
        links: [{ label: "Journal & guides", to: "/journal" }],
      };
    case "netrates":
      return {
        from: "bot",
        text: "Happy to share confidential net rates for the trade. Send your dates, pax & destination on WhatsApp or via the enquiry form:",
        links: [{ label: "Enquiry form", to: "/contact" }],
        wa: "Hi TripTrix, I'd like net rates. Here are my dates/pax/destination:",
      };
    case "human":
      return {
        from: "bot",
        text: "Sure — reach our trade desk directly:",
        wa: "Hi TripTrix, I'd like to speak with your trade desk.",
        call: true,
      };
    case "callback":
      return {
        from: "bot",
        text: "Sure — leave your phone number below and our trade desk will call you back with confidential net rates.",
      };
    default:
      return GREETING;
  }
}

export default function FloatingActions() {
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [lead, setLead] = useState({ name: "", phone: "" });
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, chatOpen, leadOpen]);

  const pushQuick = (id) => {
    setMessages((m) => [...m, { from: "user", text: QUICK.find((q) => q.id === id)?.label || id }, botReply(id)]);
    if (id === "callback" || id === "netrates") setLeadOpen(true);
  };

  const sendLead = async () => {
    const phone = lead.phone.trim();
    if (phone.replace(/[^0-9]/g, "").length < 7) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    setLeadLoading(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, {
        name: lead.name.trim() || "Chatbot visitor",
        phone,
        message: "Requested a callback via the website chat assistant.",
        source: "Chatbot",
      });
      setLeadSent(true);
      setMessages((m) => [
        ...m,
        { from: "bot", text: `Thank you! Our trade desk will call you shortly on ${phone}. Your reference is ${data.ref}.` },
      ]);
      toast.success("Callback request sent");
    } catch (e) {
      toast.error("Could not send. Please try WhatsApp or call us.");
    } finally {
      setLeadLoading(false);
    }
  };

  const handleSend = async () => {
    const t = input.trim();
    if (!t) return;
    setMessages((m) => [...m, { from: "user", text: t }]);
    setInput("");
    const reply = await getBotAnswer(t);
    setMessages((m) => [...m, reply]);
    if (reply.openLead) setLeadOpen(true);
  };

  return (
    <>
      {/* Floating buttons */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3" data-testid="floating-actions">
        <a
          href={`tel:${SITE.phoneRaw}`}
          data-testid="fab-call"
          aria-label="Call us"
          className="grid h-12 w-12 place-items-center rounded-full bg-[var(--tx-gold)] text-[var(--tx-bg)] shadow-lg shadow-black/40 hover:scale-105 transition-transform duration-300"
        >
          <Phone className="h-5 w-5" />
        </a>
        <a
          href={waLink()}
          target="_blank"
          rel="noreferrer"
          data-testid="whatsapp-fab"
          aria-label="Chat on WhatsApp"
          className="relative grid h-14 w-14 place-items-center rounded-full bg-[#25D366] text-white shadow-lg shadow-black/40 hover:scale-105 transition-transform duration-300"
        >
          <MessageCircle className="h-7 w-7" />
          <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-20" />
        </a>
        <button
          onClick={() => setChatOpen((v) => !v)}
          data-testid="chatbot-toggle"
          aria-label="Open chat assistant"
          className="grid h-12 w-12 place-items-center rounded-full bg-[var(--tx-bg-2)] border border-[var(--tx-gold)] text-[var(--tx-gold)] shadow-lg shadow-black/40 hover:scale-105 transition-transform duration-300"
        >
          {chatOpen ? <X className="h-5 w-5" /> : <Bot className="h-5 w-5" />}
        </button>
      </div>

      {/* Chat panel */}
      <AnimatePresence>
        {chatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            data-testid="chatbot-panel"
            className="fixed bottom-24 right-6 z-50 flex h-[30rem] w-[22rem] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-2xl border border-[var(--tx-border)] bg-[var(--tx-bg-2)] shadow-2xl shadow-black/50"
          >
            <div className="flex items-center gap-3 border-b border-[var(--tx-border)] bg-[var(--tx-bg)] px-4 py-3">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-[var(--tx-gold)] text-[var(--tx-bg)]"><Bot className="h-5 w-5" /></span>
              <div className="leading-tight">
                <p className="text-sm font-medium">Trixie · TripTrix Assistant</p>
                <p className="text-[0.65rem] text-[#25D366]">● Online · free to chat</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="chatbot-messages">
              {messages.map((m, i) => (
                <div key={i} className={m.from === "user" ? "flex justify-end" : "flex justify-start"}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.from === "user" ? "bg-[var(--tx-gold)] text-[var(--tx-bg)]" : "bg-[var(--tx-bg)] text-[var(--tx-text)] border border-[var(--tx-border)]"}`}>
                    <p>{m.text}</p>
                    {m.links?.map((l) => (
                      <Link key={l.to} to={l.to} onClick={() => setChatOpen(false)} className="mt-2 block text-[var(--tx-gold)] underline underline-offset-2 line-clamp-1">
                        → {l.label}
                      </Link>
                    ))}
                    {m.wa && (
                      <a href={waLink(m.wa)} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-3 py-1.5 text-xs font-medium text-white">
                        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp us
                      </a>
                    )}
                    {m.call && (
                      <a href={`tel:${SITE.phoneRaw}`} className="mt-2 ml-2 inline-flex items-center gap-1.5 rounded-full border border-[var(--tx-gold)] px-3 py-1.5 text-xs font-medium text-[var(--tx-gold)]">
                        <Phone className="h-3.5 w-3.5" /> Call
                      </a>
                    )}
                  </div>
                </div>
              ))}

              <div className="flex flex-wrap gap-2 pt-1">
                {QUICK.map((q) => (
                  <button
                    key={q.id}
                    onClick={() => pushQuick(q.id)}
                    data-testid={`chatbot-quick-${q.id}`}
                    className="rounded-full border border-[var(--tx-border)] px-3 py-1.5 text-xs text-[var(--tx-text-2)] hover:border-[var(--tx-gold)] hover:text-[var(--tx-gold)] transition-colors duration-200"
                  >
                    {q.label}
                  </button>
                ))}
              </div>

              {leadOpen && !leadSent && (
                <div data-testid="chatbot-lead-form" className="mt-3 rounded-xl border border-[var(--tx-gold)]/40 bg-[var(--tx-bg)] p-3">
                  <p className="mb-2 text-xs text-[var(--tx-text-2)]">Leave your number — our trade desk will call you back with net rates.</p>
                  <input
                    value={lead.name}
                    onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))}
                    data-testid="chatbot-lead-name"
                    placeholder="Name / agency (optional)"
                    className="mb-2 w-full rounded-full border border-[var(--tx-border)] bg-[var(--tx-bg-2)] px-4 py-2 text-sm focus:border-[var(--tx-gold)] focus:outline-none"
                  />
                  <div className="flex items-center gap-2">
                    <input
                      value={lead.phone}
                      onChange={(e) => setLead((l) => ({ ...l, phone: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && sendLead()}
                      data-testid="chatbot-lead-phone"
                      inputMode="tel"
                      placeholder="+91 phone / WhatsApp"
                      className="w-full rounded-full border border-[var(--tx-border)] bg-[var(--tx-bg-2)] px-4 py-2 text-sm focus:border-[var(--tx-gold)] focus:outline-none"
                    />
                    <button
                      onClick={sendLead}
                      disabled={leadLoading}
                      data-testid="chatbot-lead-submit"
                      aria-label="Send my number"
                      className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--tx-gold)] text-[var(--tx-bg)] disabled:opacity-60"
                    >
                      {leadLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 border-t border-[var(--tx-border)] p-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                data-testid="chatbot-input"
                placeholder="Ask me anything — e.g. best time for Sikkim, ILP permits…"
                className="w-full rounded-full border border-[var(--tx-border)] bg-[var(--tx-bg)] px-4 py-2 text-sm focus:border-[var(--tx-gold)] focus:outline-none"
              />
              <button onClick={handleSend} data-testid="chatbot-send" aria-label="Send" className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--tx-gold)] text-[var(--tx-bg)]">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
